// The consolidated version journey: a T3 dropdown listing every scored checkpoint (first draft,
// each Taka revise, your coalesced edits, restores) newest-first. Each row previews on click and
// can be restored. This replaces the old "How this was made" trace — the journey now lives in one
// place, on the composer control bar's stepper.

import { Check, RotateCcw } from 'lucide-react'
import { scoreTier } from '../../process/creative-process.types'
import { tierColor } from '../../components/process/tier-color'
import { useAttachmentUrl } from '../use-attachment-url'
import type { CreativeVersionSummary } from '../../store/versions-store'
import { kindIcon, relativeTime } from './version-format'

function VersionRow({
  version,
  prevScore,
  isCurrent,
  isTip,
  onPreview,
  onRestore,
}: {
  version: CreativeVersionSummary
  prevScore?: number | null
  isCurrent: boolean
  isTip: boolean
  onPreview: () => void
  onRestore: () => void
}) {
  const thumb = useAttachmentUrl(version.masterAttachmentId ?? undefined)
  const score = version.score ?? null
  const color = score != null ? tierColor(scoreTier(score)) : 'var(--paper-faint)'
  const delta = score != null && prevScore != null && prevScore !== score ? score - prevScore : null

  return (
    <div
      className={`flex w-full items-center gap-2 px-2 py-1.5 text-left transition-colors hover:bg-bg-3 ${
        isCurrent ? 'bg-bg-3/60' : ''
      }`}
    >
      <button type="button" onClick={onPreview} className="flex min-w-0 flex-1 items-center gap-2">
        {/* Thumbnail (rendered master) or a kind glyph fallback. */}
        <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border border-line bg-bg-4">
          {thumb ? (
            <img src={thumb} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-paper-faint">
              {kindIcon(version.kind)}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="flex items-center gap-1 text-[12px] font-medium text-paper">
              {kindIcon(version.kind)}
              {version.label ?? `v${version.seq}`}
            </span>
            {isTip && (
              <span className="rounded bg-bg-4 px-1 text-[9px] font-medium tracking-wide text-paper-faint uppercase">
                Latest
              </span>
            )}
          </div>
          {version.feedback && (
            <p className="truncate text-[11px] text-paper-faint">{version.feedback}</p>
          )}
        </div>

        {/* Score dot + delta. */}
        <div className="flex flex-shrink-0 flex-col items-end">
          <span
            className="flex items-center gap-1 text-[12px] font-semibold tabular-nums"
            style={{ color }}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: color }} />
            {score ?? '—'}
          </span>
          <span className="text-[10px] text-paper-faint tabular-nums">
            {delta != null ? `${delta > 0 ? '+' : ''}${delta} · ` : ''}
            {relativeTime(version.createdAt)}
          </span>
        </div>
      </button>

      <div className="flex w-5 flex-shrink-0 justify-center">
        {isCurrent ? (
          <Check className="h-3.5 w-3.5 text-paper" />
        ) : (
          <button
            type="button"
            onClick={onRestore}
            title={`Restore ${version.label ?? `v${version.seq}`}`}
            className="rounded-full p-1 text-paper-faint transition-colors hover:bg-bg-4 hover:text-paper"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}

export function VersionsTimeline({
  versions,
  currentSeq,
  onPreview,
  onRestore,
}: {
  versions: CreativeVersionSummary[]
  currentSeq: number | null
  onPreview: (seq: number) => void
  onRestore: (seq: number) => void
}) {
  if (versions.length === 0) return null
  const tipSeq = versions[versions.length - 1]!.seq
  const rows = [...versions].reverse()

  return (
    <div className="absolute top-full right-0 z-50 mt-1.5 max-h-[60vh] w-72 overflow-y-auto rounded-xl border border-line bg-bg-2 py-1 text-paper shadow-[var(--shadow-card)]">
      <div className="border-b border-line px-3 py-1.5 text-[10px] font-medium tracking-[0.15em] text-paper-faint uppercase">
        Versions
      </div>
      {rows.map(v => {
        const idx = versions.findIndex(x => x.seq === v.seq)
        const prev = idx > 0 ? versions[idx - 1]!.score : null
        return (
          <VersionRow
            key={v.seq}
            version={v}
            prevScore={prev}
            isCurrent={v.seq === currentSeq}
            isTip={v.seq === tipSeq}
            onPreview={() => onPreview(v.seq)}
            onRestore={() => onRestore(v.seq)}
          />
        )
      })}
    </div>
  )
}
